import React from 'react';
import { Monitor, Video, Phone, ArrowRight, CheckCircle, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import FAQRichSnippet from '../../components/FAQRichSnippet';

const TELEHEALTH_FAQS = [
  { q: "How do telehealth appointments work?", a: "You connect with your provider via a secure video platform from any private location with internet access. Your provider will send a link before the appointment. All you need is a device with a camera and microphone." },
  { q: "Is telehealth as effective as in-person therapy?", a: "Research shows telehealth therapy is comparably effective to in-person therapy for most conditions including depression, anxiety, and PTSD. Your provider will recommend the best modality for your needs." },
  { q: "Do I need to be in Maryland for telehealth?", a: "Yes. Our providers are licensed in Maryland, so you must be physically located in the state during your telehealth session." },
  { q: "Is telehealth covered by my insurance?", a: "Yes. Medicaid, Maryland HealthChoice MCOs, and most commercial insurance plans cover telehealth services. Contact us to verify your specific coverage." },
];

const TelehealthPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Telehealth Services"
        description="Telehealth mental health services from SDORA Wellbeing. Virtual therapy, psychiatric evaluation, medication management, and PRP sessions for clients in Maryland. HIPAA-compliant video visits. Accepting Medicaid."
        path="/services/telehealth"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-cyan-600 to-blue-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Monitor className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">Telehealth Services</h1>
          <p className="text-lg text-cyan-100 max-w-2xl mx-auto">
            Access quality mental health care from the comfort of your home. Secure, HIPAA-compliant virtual sessions with our licensed clinicians.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* What is Telehealth */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Mental Health Care, Wherever You Are</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            SDORA Wellbeing offers telehealth as a convenient, effective way to receive mental health services 
            without traveling to our office. Using secure, HIPAA-compliant video technology, you can meet with your 
            therapist, psychiatrist, or case manager from your home, workplace, or any private location.
          </p>
          <p className="text-gray-700 leading-relaxed">
            Telehealth is especially helpful for clients with transportation challenges, busy schedules, physical 
            health limitations, or those who prefer the comfort and privacy of their own space.
          </p>
        </section>

        {/* Services Available */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Services Available via Telehealth</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Individual therapy (CBT, DBT, trauma-focused)",
              "Family and couples therapy",
              "Psychiatric evaluation and medication management",
              "Psychiatric Rehabilitation (PRP) sessions",
              "Case management and care coordination",
              "Comprehensive mental health assessments",
              "Student counseling sessions",
              "Anger management counseling",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Telehealth availability may vary by service type, clinician licensure, payer requirements, and clinical appropriateness. 
            Not all services may be available via telehealth. Contact us to confirm availability for your specific needs.
          </p>
        </section>

        {/* How it Works */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-4">How Telehealth Works</h2>
          <div className="space-y-4">
            {[
              ["1. Schedule", "Call us or submit a referral to request a telehealth appointment."],
              ["2. Prepare", "You'll receive a secure link to join your session. All you need is a device with a camera and internet connection."],
              ["3. Connect", "Join your session at the scheduled time. Your clinician will be ready to meet with you."],
              ["4. Continue", "Follow-up sessions, medication adjustments, and ongoing care can all continue via telehealth."],
            ].map(([step, desc]) => (
              <div key={step} className="flex items-start gap-4 p-4 bg-cyan-50 rounded-lg border border-cyan-100">
                <span className="text-sm font-bold text-cyan-700 whitespace-nowrap">{step}</span>
                <p className="text-sm text-gray-700">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Privacy */}
        <section className="mb-12 bg-blue-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="text-base font-bold text-gray-900 mb-2">Privacy & Security</h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                All telehealth sessions at SDORA Wellbeing use HIPAA-compliant technology to protect your privacy. 
                Your session is encrypted end-to-end, and we follow the same confidentiality standards as in-person visits.
              </p>
            </div>
          </div>
        </section>

        {/* Insurance */}
        <section className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Insurance Coverage for Telehealth</h2>
          <p className="text-gray-700 leading-relaxed mb-2">
            Most insurance plans, including Medicaid and Maryland HealthChoice MCOs, cover telehealth mental health services. 
            Coverage varies by plan. Contact us or your insurance provider to verify your specific telehealth benefits.
          </p>
          <Link to="/insurance" className="text-sm text-cyan-700 font-semibold hover:underline inline-flex items-center gap-1">
            View accepted insurance plans <ArrowRight className="w-3 h-3" />
          </Link>
        </section>

        {/* CTA */}
        <section className="text-center bg-gray-50 rounded-xl p-8 border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Start Your Telehealth Journey</h2>
          <p className="text-sm text-gray-600 mb-4">Schedule a virtual appointment with one of our licensed clinicians today.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+18777725150" className="inline-flex items-center gap-2 bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors">
              <Phone className="w-4 h-4" /> (877) 772-5150
            </a>
            <Link to="/get-started" className="inline-flex items-center gap-2 bg-white text-cyan-600 border border-cyan-200 px-6 py-3 rounded-lg font-semibold hover:bg-cyan-50 transition-colors">
              Get Started <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
        <FAQRichSnippet faqs={TELEHEALTH_FAQS} />
      </div>

      <Footer />
    </div>
  );
};

export default TelehealthPage;
